import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { impuestosPageService } from '@/services/impuestosPageService'
import type { CatalogoConMetricas, HuecoVencimiento, ImpuestosPageStats, PosicionFiscalIVA, ResumenIVAMes } from '@/services/impuestosPageService'

export type { CatalogoConMetricas, HuecoVencimiento, ImpuestosPageStats, PosicionFiscalIVA, ResumenIVAMes }

export const impuestosPageKeys = {
  all: ['impuestos-page'] as const,
  catalogo: (orgId: string) => [...impuestosPageKeys.all, 'catalogo', orgId] as const,
  stats: (orgId: string) => [...impuestosPageKeys.all, 'stats', orgId] as const,
  detalle: (impuestoId: string, orgId: string) => [...impuestosPageKeys.all, 'detalle', impuestoId, orgId] as const,
  posicionFiscal: (orgId: string, anio: number) => [...impuestosPageKeys.all, 'posicion-fiscal', orgId, anio] as const,
  huecos: (orgId: string) => [...impuestosPageKeys.all, 'huecos', orgId] as const,
}

const DEFAULT_STATS: ImpuestosPageStats = {
  total_impuestos: 0,
  total_clientes_con_impuestos: 0,
  pendientes_este_mes: 0,
  vencidos: 0,
}

export function useImpuestosPageData() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  const enabled = !!supabase && !supabaseLoading && !!organizacionId

  const catalogoQuery = useQuery({
    queryKey: impuestosPageKeys.catalogo(organizacionId ?? ''),
    queryFn: () => impuestosPageService.obtenerCatalogoConMetricas(supabase!, organizacionId!),
    enabled,
    staleTime: 30000,
  })

  const statsQuery = useQuery({
    queryKey: impuestosPageKeys.stats(organizacionId ?? ''),
    queryFn: () => impuestosPageService.obtenerEstadisticas(supabase!, organizacionId!),
    enabled,
    staleTime: 30000,
  })

  return {
    catalogo: catalogoQuery.data ?? ([] as CatalogoConMetricas[]),
    stats: statsQuery.data ?? DEFAULT_STATS,
    isLoading: catalogoQuery.isLoading || statsQuery.isLoading,
    error: catalogoQuery.error ?? statsQuery.error,
  }
}

export function useImpuestoDetalle(impuestoId: string | null) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: impuestosPageKeys.detalle(impuestoId ?? '', organizacionId ?? ''),
    queryFn: () => impuestosPageService.obtenerDetalleImpuesto(supabase!, impuestoId!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!impuestoId && !!organizacionId,
    staleTime: 30000,
  })
}

export function usePosicionFiscalIVA(anio: number) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: impuestosPageKeys.posicionFiscal(organizacionId ?? '', anio),
    queryFn: () => impuestosPageService.obtenerPosicionFiscalIVA(supabase!, organizacionId!, anio),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    staleTime: 60000,
  })
}

/** ORR-268: impuestos activos que dejaron de generar vencimientos */
export function useHuecosVencimientos() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: impuestosPageKeys.huecos(organizacionId ?? ''),
    queryFn: () => impuestosPageService.detectarHuecosVencimientos(supabase!, organizacionId!),
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    staleTime: 60000,
  })
}
